import { useState } from 'react';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const inputCls = [
  'w-full px-4 py-3.5 text-[14px] outline-none transition-colors duration-200',
  'bg-white border border-[#1A1A1A]/12 text-[#061e45]',
  'placeholder:text-[#1A1A1A]/30 focus:border-[#061e45]',
].join(' ');

const labelCls = 'block text-[#061e45] text-[12px] uppercase tracking-[0.1em] font-medium mb-1.5';

const STEPS = [
  { num: 1, label: 'Business Info' },
  { num: 2, label: 'Principal' },
  { num: 3, label: 'Processing' },
  { num: 4, label: 'Banking' },
  { num: 5, label: 'Review & Sign' },
];

type FD = {
  legalName: string; dba: string; businessType: string; ein: string;
  dateEstablished: string; stateOfInc: string; website: string;
  businessPhone: string; businessStreet: string; businessCity: string;
  businessState: string; businessZip: string;
  ownerFirst: string; ownerLast: string; ownerTitle: string;
  ownerDob: string; ownerSsn: string; ownerPct: string;
  ownerStreet: string; ownerCity: string; ownerState: string; ownerZip: string;
  ownerEmail: string; ownerPhone: string;
  productsServices: string; monthlyVolume: string; avgTicket: string;
  highTicket: string; pctCardPresent: string; pctEcommerce: string;
  pctMoto: string; currentProcessor: string; reasonSwitching: string;
  bankName: string; routingNumber: string; accountNumber: string; accountType: string;
  signature: string; agreedToTerms: boolean; agreedToAuth: boolean;
};

const EMPTY: FD = {
  legalName: '', dba: '', businessType: '', ein: '', dateEstablished: '',
  stateOfInc: '', website: '', businessPhone: '', businessStreet: '',
  businessCity: '', businessState: '', businessZip: '',
  ownerFirst: '', ownerLast: '', ownerTitle: '', ownerDob: '', ownerSsn: '',
  ownerPct: '', ownerStreet: '', ownerCity: '', ownerState: '', ownerZip: '',
  ownerEmail: '', ownerPhone: '',
  productsServices: '', monthlyVolume: '', avgTicket: '', highTicket: '',
  pctCardPresent: '', pctEcommerce: '', pctMoto: '', currentProcessor: '', reasonSwitching: '',
  bankName: '', routingNumber: '', accountNumber: '', accountType: '',
  signature: '', agreedToTerms: false, agreedToAuth: false,
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className={labelCls}>
        {label}{required && <span className="text-[#d3b366] ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function Select({ name, value, onChange, children, required }: {
  name: string; value: string; onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  children: React.ReactNode; required?: boolean;
}) {
  return (
    <div className="relative">
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className={[inputCls, 'appearance-none pr-10 cursor-pointer'].join(' ')}
        style={{ color: value ? '#061e45' : 'rgba(26,26,26,0.3)' }}
      >
        {children}
      </select>
      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#061e45]/40" />
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#1A1A1A]/8">
      <div className="w-0.5 h-5 bg-[#d3b366] shrink-0" />
      <h3 className="text-[#061e45] text-lg font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
        {children}
      </h3>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex gap-3">
      <span className="text-[#1A1A1A]/45 text-[12px] uppercase tracking-[0.08em] shrink-0 w-44">{label}</span>
      <span className="text-[#061e45] text-sm">{value}</span>
    </div>
  );
}

export default function ApplicationForm() {
  const [step, setStep] = useState(1);
  const [fd, setFd] = useState<FD>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFd(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const validate = (s: number): string => {
    if (s === 1) {
      if (!fd.legalName.trim()) return 'Legal business name is required.';
      if (!fd.businessType) return 'Business type is required.';
      if (!fd.ein.trim()) return 'Federal Tax ID (EIN) is required.';
      if (!fd.businessPhone.trim()) return 'Business phone is required.';
      if (!fd.businessStreet.trim() || !fd.businessCity.trim() || !fd.businessState || !fd.businessZip.trim())
        return 'Complete business address is required.';
    }
    if (s === 2) {
      if (!fd.ownerFirst.trim() || !fd.ownerLast.trim()) return 'Owner full name is required.';
      if (!fd.ownerTitle.trim()) return 'Owner title is required.';
      if (!fd.ownerDob) return 'Date of birth is required.';
      if (!fd.ownerSsn.trim()) return 'Social Security Number is required.';
      if (!fd.ownerPct.trim()) return 'Ownership percentage is required.';
      if (!fd.ownerEmail.trim()) return 'Owner email is required.';
      if (!fd.ownerPhone.trim()) return 'Owner phone is required.';
      if (!fd.ownerStreet.trim() || !fd.ownerCity.trim() || !fd.ownerState || !fd.ownerZip.trim())
        return 'Complete home address is required.';
    }
    if (s === 3) {
      if (!fd.productsServices.trim()) return 'Please describe your products/services.';
      if (!fd.monthlyVolume) return 'Estimated monthly volume is required.';
      if (!fd.avgTicket.trim()) return 'Average ticket amount is required.';
      const cp = Number(fd.pctCardPresent || 0);
      const ec = Number(fd.pctEcommerce || 0);
      const mo = Number(fd.pctMoto || 0);
      if (cp + ec + mo !== 100) return 'Card Present + eCommerce + MOTO percentages must add up to 100%.';
    }
    if (s === 4) {
      if (!fd.bankName.trim()) return 'Bank name is required.';
      if (!fd.routingNumber.trim()) return 'Routing number is required.';
      if (!fd.accountNumber.trim()) return 'Account number is required.';
      if (!fd.accountType) return 'Account type is required.';
    }
    if (s === 5) {
      if (!fd.signature.trim()) return 'Electronic signature is required.';
      if (!fd.agreedToTerms) return 'You must agree to the Terms of Service.';
      if (!fd.agreedToAuth) return 'You must authorize the application.';
    }
    return '';
  };

  const next = () => {
    const err = validate(step);
    if (err) { setError(err); return; }
    setError('');
    setStep(s => (s + 1) as typeof step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setError('');
    setStep(s => (s - 1) as typeof step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    const err = validate(5);
    if (err) { setError(err); return; }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fd),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white border border-[#1A1A1A]/8 p-10 md:p-16 text-center">
        <div className="w-14 h-14 flex items-center justify-center bg-[#d3b366]/15 mx-auto mb-6">
          <i className="ri-check-line text-[#a8894a] text-3xl" />
        </div>
        <h3 className="text-[#061e45] text-3xl font-light mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
          Application Received.
        </h3>
        <p className="text-[#1A1A1A]/55 text-[15px] leading-relaxed max-w-md mx-auto">
          Thank you for applying. A member of our team will review your application and reach out within 1–2 business days.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Progress bar */}
      <div className="mb-10">
        <div className="flex items-center gap-0">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex-1 flex flex-col items-center gap-2 relative">
              {i < STEPS.length - 1 && (
                <div
                  className="absolute left-1/2 top-4 w-full h-px"
                  style={{ background: step > s.num ? '#d3b366' : 'rgba(6,30,69,0.1)' }}
                />
              )}
              <div
                className="relative z-10 w-8 h-8 flex items-center justify-center text-[11px] font-semibold transition-colors duration-200"
                style={{
                  background: step === s.num ? '#061e45' : step > s.num ? '#d3b366' : 'white',
                  border: step === s.num || step > s.num ? 'none' : '1px solid rgba(6,30,69,0.15)',
                  color: step === s.num ? 'white' : step > s.num ? '#061e45' : 'rgba(6,30,69,0.35)',
                }}
              >
                {step > s.num ? <i className="ri-check-line" /> : s.num}
              </div>
              <span
                className="text-[10px] uppercase tracking-[0.1em] text-center hidden sm:block"
                style={{ color: step === s.num ? '#061e45' : 'rgba(6,30,69,0.35)' }}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#1A1A1A]/8 p-8 md:p-10">

        {/* ── Step 1: Business Information ── */}
        {step === 1 && (
          <div className="flex flex-col gap-6">
            <SectionTitle>Business Information</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Legal Business Name" required>
                <input name="legalName" value={fd.legalName} onChange={set} className={inputCls} placeholder="As it appears on tax documents" />
              </Field>
              <Field label="DBA / Trade Name">
                <input name="dba" value={fd.dba} onChange={set} className={inputCls} placeholder="If different from legal name" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Business Type" required>
                <Select name="businessType" value={fd.businessType} onChange={set} required>
                  <option value="" disabled>Select type</option>
                  <option>Sole Proprietor</option>
                  <option>LLC</option>
                  <option>S-Corporation</option>
                  <option>C-Corporation</option>
                  <option>Partnership</option>
                  <option>Non-Profit</option>
                </Select>
              </Field>
              <Field label="Federal Tax ID (EIN)" required>
                <input name="ein" value={fd.ein} onChange={set} className={inputCls} placeholder="XX-XXXXXXX" maxLength={10} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Date Business Established" required>
                <input type="date" name="dateEstablished" value={fd.dateEstablished} onChange={set} className={inputCls} />
              </Field>
              <Field label="State of Incorporation" required>
                <Select name="stateOfInc" value={fd.stateOfInc} onChange={set}>
                  <option value="" disabled>Select state</option>
                  {US_STATES.map(s => <option key={s}>{s}</option>)}
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Business Website">
                <input name="website" value={fd.website} onChange={set} className={inputCls} placeholder="https://" />
              </Field>
              <Field label="Business Phone" required>
                <input type="tel" name="businessPhone" value={fd.businessPhone} onChange={set} className={inputCls} placeholder="(555) 000-0000" />
              </Field>
            </div>
            <SectionTitle>Business Address</SectionTitle>
            <Field label="Street Address" required>
              <input name="businessStreet" value={fd.businessStreet} onChange={set} className={inputCls} placeholder="123 Main St" />
            </Field>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div className="col-span-2">
                <Field label="City" required>
                  <input name="businessCity" value={fd.businessCity} onChange={set} className={inputCls} />
                </Field>
              </div>
              <Field label="State" required>
                <Select name="businessState" value={fd.businessState} onChange={set}>
                  <option value="" disabled>ST</option>
                  {US_STATES.map(s => <option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="ZIP" required>
                <input name="businessZip" value={fd.businessZip} onChange={set} className={inputCls} maxLength={10} />
              </Field>
            </div>
          </div>
        )}

        {/* ── Step 2: Principal / Owner ── */}
        {step === 2 && (
          <div className="flex flex-col gap-6">
            <SectionTitle>Principal / Owner Information</SectionTitle>
            <p className="text-[#1A1A1A]/50 text-sm -mt-2 mb-2">
              Required for identity verification. If there are multiple owners with 25%+ ownership, each will need to be disclosed.
              This form is SSL-encrypted and your data is handled securely.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="First Name" required>
                <input name="ownerFirst" value={fd.ownerFirst} onChange={set} className={inputCls} />
              </Field>
              <Field label="Last Name" required>
                <input name="ownerLast" value={fd.ownerLast} onChange={set} className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Title / Role" required>
                <input name="ownerTitle" value={fd.ownerTitle} onChange={set} className={inputCls} placeholder="e.g. CEO, Owner, President" />
              </Field>
              <Field label="Ownership %" required>
                <input name="ownerPct" value={fd.ownerPct} onChange={set} className={inputCls} placeholder="e.g. 100" type="number" min="1" max="100" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Date of Birth" required>
                <input type="date" name="ownerDob" value={fd.ownerDob} onChange={set} className={inputCls} />
              </Field>
              <Field label="Social Security Number" required>
                <input type="password" name="ownerSsn" value={fd.ownerSsn} onChange={set} className={inputCls} placeholder="XXX-XX-XXXX" autoComplete="off" maxLength={11} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Email Address" required>
                <input type="email" name="ownerEmail" value={fd.ownerEmail} onChange={set} className={inputCls} />
              </Field>
              <Field label="Phone Number" required>
                <input type="tel" name="ownerPhone" value={fd.ownerPhone} onChange={set} className={inputCls} placeholder="(555) 000-0000" />
              </Field>
            </div>
            <SectionTitle>Home Address</SectionTitle>
            <Field label="Street Address" required>
              <input name="ownerStreet" value={fd.ownerStreet} onChange={set} className={inputCls} placeholder="123 Main St" />
            </Field>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              <div className="col-span-2">
                <Field label="City" required>
                  <input name="ownerCity" value={fd.ownerCity} onChange={set} className={inputCls} />
                </Field>
              </div>
              <Field label="State" required>
                <Select name="ownerState" value={fd.ownerState} onChange={set}>
                  <option value="" disabled>ST</option>
                  {US_STATES.map(s => <option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="ZIP" required>
                <input name="ownerZip" value={fd.ownerZip} onChange={set} className={inputCls} maxLength={10} />
              </Field>
            </div>
          </div>
        )}

        {/* ── Step 3: Processing Details ── */}
        {step === 3 && (
          <div className="flex flex-col gap-6">
            <SectionTitle>Processing Details</SectionTitle>
            <Field label="Products / Services Sold" required>
              <textarea
                name="productsServices"
                value={fd.productsServices}
                onChange={set}
                rows={3}
                className={[inputCls, 'resize-none'].join(' ')}
                placeholder="Describe what your business sells and how customers typically pay"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <Field label="Est. Monthly Volume" required>
                <Select name="monthlyVolume" value={fd.monthlyVolume} onChange={set} required>
                  <option value="" disabled>Select range</option>
                  <option>Under $50K</option>
                  <option>$50K – $250K</option>
                  <option>$250K – $500K</option>
                  <option>$500K – $1M</option>
                  <option>$1M – $5M</option>
                  <option>Over $5M</option>
                </Select>
              </Field>
              <Field label="Average Ticket ($)" required>
                <input name="avgTicket" value={fd.avgTicket} onChange={set} className={inputCls} placeholder="e.g. 85" />
              </Field>
              <Field label="Highest Ticket ($)">
                <input name="highTicket" value={fd.highTicket} onChange={set} className={inputCls} placeholder="e.g. 5000" />
              </Field>
            </div>
            <div>
              <p className={labelCls}>How Sales Are Accepted <span className="text-[#d3b366]">*</span></p>
              <p className="text-[#1A1A1A]/45 text-[11px] mb-3">Must total 100%</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label="Card Present (in-person) %">
                  <input name="pctCardPresent" value={fd.pctCardPresent} onChange={set} className={inputCls} placeholder="e.g. 70" type="number" min="0" max="100" />
                </Field>
                <Field label="eCommerce (online) %">
                  <input name="pctEcommerce" value={fd.pctEcommerce} onChange={set} className={inputCls} placeholder="e.g. 20" type="number" min="0" max="100" />
                </Field>
                <Field label="MOTO (keyed / phone) %">
                  <input name="pctMoto" value={fd.pctMoto} onChange={set} className={inputCls} placeholder="e.g. 10" type="number" min="0" max="100" />
                </Field>
              </div>
              {(fd.pctCardPresent || fd.pctEcommerce || fd.pctMoto) && (
                <p
                  className="mt-2 text-[12px]"
                  style={{
                    color: Number(fd.pctCardPresent || 0) + Number(fd.pctEcommerce || 0) + Number(fd.pctMoto || 0) === 100
                      ? '#a8894a' : 'rgba(200,50,50,0.8)'
                  }}
                >
                  Total: {Number(fd.pctCardPresent || 0) + Number(fd.pctEcommerce || 0) + Number(fd.pctMoto || 0)}%
                  {Number(fd.pctCardPresent || 0) + Number(fd.pctEcommerce || 0) + Number(fd.pctMoto || 0) === 100 ? ' ✓' : ' (must equal 100%)'}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Current Processor">
                <input name="currentProcessor" value={fd.currentProcessor} onChange={set} className={inputCls} placeholder="e.g. Square, Toast, Stripe" />
              </Field>
              <Field label="Have you had prior chargebacks or terminations?">
                <Select name="reasonSwitching" value={fd.reasonSwitching} onChange={set}>
                  <option value="" disabled>Select</option>
                  <option value="No prior issues">No prior issues</option>
                  <option value="Yes — chargebacks">Yes — chargebacks</option>
                  <option value="Yes — terminated">Yes — terminated</option>
                </Select>
              </Field>
            </div>
            <Field label="Reason for Switching Processors">
              <textarea
                name="reasonSwitching"
                value={fd.reasonSwitching}
                onChange={set}
                rows={2}
                className={[inputCls, 'resize-none'].join(' ')}
                placeholder="Optional — helps us understand your needs"
              />
            </Field>
          </div>
        )}

        {/* ── Step 4: Banking ── */}
        {step === 4 && (
          <div className="flex flex-col gap-6">
            <SectionTitle>Bank Settlement Information</SectionTitle>
            <p className="text-[#1A1A1A]/50 text-sm -mt-2 mb-2">
              This account will be used for daily settlement of your processing deposits. Your banking information is transmitted over SSL encryption.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Bank Name" required>
                <input name="bankName" value={fd.bankName} onChange={set} className={inputCls} placeholder="e.g. JPMorgan Chase" />
              </Field>
              <Field label="Account Type" required>
                <Select name="accountType" value={fd.accountType} onChange={set} required>
                  <option value="" disabled>Select type</option>
                  <option>Checking</option>
                  <option>Savings</option>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Routing Number (ABA)" required>
                <input name="routingNumber" value={fd.routingNumber} onChange={set} className={inputCls} placeholder="9-digit routing number" maxLength={9} />
              </Field>
              <Field label="Account Number" required>
                <input type="password" name="accountNumber" value={fd.accountNumber} onChange={set} className={inputCls} placeholder="Account number" autoComplete="off" />
              </Field>
            </div>
            <div className="p-5 bg-[#F9F9F7] border border-[#1A1A1A]/8 flex items-start gap-3">
              <i className="ri-shield-check-line text-[#a8894a] text-xl shrink-0 mt-0.5" />
              <p className="text-[#1A1A1A]/60 text-[13px] leading-relaxed">
                Your banking details are encrypted in transit and at rest. We use this information solely for ACH settlement and do not share it with third parties.
              </p>
            </div>
          </div>
        )}

        {/* ── Step 5: Review & Sign ── */}
        {step === 5 && (
          <div className="flex flex-col gap-8">
            <SectionTitle>Review Your Application</SectionTitle>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex flex-col gap-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#a8894a] font-medium mb-1">Business</p>
                <ReviewRow label="Legal Name" value={fd.legalName} />
                <ReviewRow label="DBA" value={fd.dba} />
                <ReviewRow label="Type" value={fd.businessType} />
                <ReviewRow label="EIN" value={fd.ein} />
                <ReviewRow label="Established" value={fd.dateEstablished} />
                <ReviewRow label="State of Inc." value={fd.stateOfInc} />
                <ReviewRow label="Phone" value={fd.businessPhone} />
                <ReviewRow label="Address" value={[fd.businessStreet, fd.businessCity, fd.businessState, fd.businessZip].filter(Boolean).join(', ')} />
              </div>
              <div className="flex flex-col gap-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#a8894a] font-medium mb-1">Principal</p>
                <ReviewRow label="Name" value={`${fd.ownerFirst} ${fd.ownerLast}`} />
                <ReviewRow label="Title" value={fd.ownerTitle} />
                <ReviewRow label="Ownership" value={fd.ownerPct ? `${fd.ownerPct}%` : ''} />
                <ReviewRow label="DOB" value={fd.ownerDob} />
                <ReviewRow label="SSN" value={fd.ownerSsn ? `***-**-${fd.ownerSsn.slice(-4)}` : ''} />
                <ReviewRow label="Email" value={fd.ownerEmail} />
                <ReviewRow label="Phone" value={fd.ownerPhone} />
              </div>
              <div className="flex flex-col gap-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#a8894a] font-medium mb-1">Processing</p>
                <ReviewRow label="Monthly Volume" value={fd.monthlyVolume} />
                <ReviewRow label="Avg Ticket" value={fd.avgTicket ? `$${fd.avgTicket}` : ''} />
                <ReviewRow label="High Ticket" value={fd.highTicket ? `$${fd.highTicket}` : ''} />
                <ReviewRow label="Card Present" value={fd.pctCardPresent ? `${fd.pctCardPresent}%` : ''} />
                <ReviewRow label="eCommerce" value={fd.pctEcommerce ? `${fd.pctEcommerce}%` : ''} />
                <ReviewRow label="MOTO" value={fd.pctMoto ? `${fd.pctMoto}%` : ''} />
                <ReviewRow label="Current Processor" value={fd.currentProcessor} />
              </div>
              <div className="flex flex-col gap-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#a8894a] font-medium mb-1">Banking</p>
                <ReviewRow label="Bank" value={fd.bankName} />
                <ReviewRow label="Account Type" value={fd.accountType} />
                <ReviewRow label="Routing #" value={fd.routingNumber} />
                <ReviewRow label="Account #" value={fd.accountNumber ? `****${fd.accountNumber.slice(-4)}` : ''} />
              </div>
            </div>

            <div className="border-t border-[#1A1A1A]/8 pt-8 flex flex-col gap-6">
              <SectionTitle>Electronic Signature & Authorization</SectionTitle>

              <div className="flex flex-col gap-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreedToTerms"
                    checked={fd.agreedToTerms}
                    onChange={set}
                    className="mt-1 accent-[#061e45] w-4 h-4 shrink-0"
                  />
                  <span className="text-[13px] text-[#1A1A1A]/65 leading-relaxed">
                    I confirm that all information provided in this application is accurate and complete to the best of my knowledge. I understand that providing false information may result in rejection or termination of processing services.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreedToAuth"
                    checked={fd.agreedToAuth}
                    onChange={set}
                    className="mt-1 accent-[#061e45] w-4 h-4 shrink-0"
                  />
                  <span className="text-[13px] text-[#1A1A1A]/65 leading-relaxed">
                    I authorize Fore Capital GRP to verify the information provided, including business and personal credit checks, and to contact references as necessary for underwriting purposes.
                  </span>
                </label>
              </div>

              <Field label="Electronic Signature — Type Your Full Legal Name" required>
                <input
                  name="signature"
                  value={fd.signature}
                  onChange={set}
                  className={[inputCls, 'italic text-[16px]'].join(' ')}
                  placeholder="Your full legal name"
                  style={{ fontFamily: 'Georgia, serif' }}
                />
              </Field>
              <p className="text-[11px] text-[#1A1A1A]/40 -mt-2">
                By typing your name above, you agree this constitutes your electronic signature with the same legal effect as a handwritten signature.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#1A1A1A]/8">
          {step > 1 ? (
            <button
              type="button"
              onClick={back}
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#1A1A1A]/15 text-[#1A1A1A]/60 text-[11px] uppercase tracking-[0.15em] font-medium hover:border-[#061e45]/40 hover:text-[#061e45] transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line" /> Back
            </button>
          ) : <div />}

          {step < 5 ? (
            <button
              type="button"
              onClick={next}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#061e45] text-white text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-[#1a3870] transition-colors duration-200 cursor-pointer"
            >
              Continue <i className="ri-arrow-right-line" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#d3b366] text-[#061e45] text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-[#a8894a] hover:text-white transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {submitting ? 'Submitting…' : 'Submit Application'}
              {!submitting && <i className="ri-send-plane-line" />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
